<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { patientsApi, type PatientFilter } from '@/api/patients';
import {
  patientScheduleBlocksApi,
  type PatientScheduleBlock,
  type ScheduleBlockPrefill,
  type ScheduleBlockConflictsParams,
} from '@/api/patient-schedule-blocks';
import { usePermissions } from '@/composables/usePermissions';
import { useAccessibleCareers } from '@/composables/useAccessibleCareers';
import { useDebouncedRef } from '@/composables/useDebouncedRef';
import { useTableFilter } from '@/composables/useTableFilter';
import { requestErrorMessage } from '@/utils/api-errors';
import { careers as allCareers, getCareerColor } from '@/utils/careers';
import TimeInput from '@/components/time-input.vue';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import CreateKindToggle from '@/components/shifts/schedule-blocks/create-kind-toggle.vue';

// Create/edit dialog for a patient schedule block ("bloqueo de horario"). Blocks are
// warning-only: the live "Turnos en este período" list is information, saving never
// touches those shifts.
interface Props {
  block?: PatientScheduleBlock;
  initial?: ScheduleBlockPrefill;
  // Opened from a patient-filtered calendar: the patient is fixed.
  lockPatient?: boolean;
  // Offers the "Turno | Bloqueo" switch back to the shift form (create only).
  showShiftToggle?: boolean;
}

const props = defineProps<Props>();

// No `refetch` event: the form invalidates both calendar queries itself, so a host
// refetch on top would only fetch the same payload twice.
const emit = defineEmits<{
  close: [],
  success: [],
  switchToShift: [],
}>();

const { t } = useI18n();
const { hasPermission } = usePermissions();
const { accessibleCareers } = useAccessibleCareers();
const queryClient = useQueryClient();
const nameFilter = useTableFilter(['fullName']);

const DESCRIPTION_MAX_LENGTH = 250;
const MAX_VISIBLE_CONFLICTS = 8;

const isEdit = computed(() => !!props.block);
const canSave = computed(() => (
  isEdit.value ? hasPermission.value('shifts', 'update') : hasPermission.value('shifts', 'create')
));
const canDestroy = computed(() => isEdit.value && hasPermission.value('shifts', 'destroy'));
const readonly = computed(() => !canSave.value);

interface BlockFormValues {
  patientId: number | undefined;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  description: string;
  careers: string[];
}

const fieldRequired = t('scheduleBlocks.validation.fieldRequired');
const validationSchema = yup.object({
  patientId: yup.number().required(fieldRequired),
  startDate: yup.string().required(fieldRequired),
  startTime: yup.string().required(fieldRequired),
  endDate: yup.string().required(fieldRequired),
  endTime: yup.string().required(fieldRequired)
    .test('end-after-start', t('scheduleBlocks.validation.endAfterStart'), function endAfterStart(value) {
      const { startDate, startTime, endDate } = this.parent as BlockFormValues;
      if (!value || !startDate || !startTime || !endDate) return true;

      return `${endDate}T${value}` > `${startDate}T${startTime}`;
    }),
  description: yup.string().trim().required(fieldRequired)
    .max(DESCRIPTION_MAX_LENGTH, t('scheduleBlocks.validation.descriptionMaxLength')),
  careers: yup.array().of(yup.string().required()),
});

function valuesFromBlock(block: PatientScheduleBlock): BlockFormValues {
  return {
    patientId: block.patientId,
    startDate: block.startDate,
    startTime: block.startTime,
    endDate: block.endDate,
    endTime: block.endTime,
    description: block.description,
    careers: [...block.careers],
  };
}

function valuesFromPrefill(prefill: ScheduleBlockPrefill | undefined): BlockFormValues {
  const empty: ScheduleBlockPrefill = { startDate: '', startTime: '', endDate: '', endTime: '' };
  const { patientId: prefillPatientId, ...range } = prefill ?? empty;

  return { patientId: prefillPatientId, ...range, description: '', careers: [] };
}

function initialValues(): BlockFormValues {
  return props.block ? valuesFromBlock(props.block) : valuesFromPrefill(props.initial);
}

const { handleSubmit, values, errors, defineField, setFieldValue } = useForm<BlockFormValues>({
  validationSchema,
  initialValues: initialValues(),
});

const [patientId, patientIdAttrs] = defineField('patientId');
const [startDate, startDateAttrs] = defineField('startDate');
const [startTime] = defineField('startTime');
const [endDate, endDateAttrs] = defineField('endDate');
const [endTime] = defineField('endTime');
const [description, descriptionAttrs] = defineField('description');
const [careers, careersAttrs] = defineField('careers');

// The end date never precedes the start date; an overnight range is typed explicitly.
watch(startDate, (value) => {
  if (value && (!endDate.value || endDate.value < value)) setFieldValue('endDate', value);
});

// Same key as the calendar filters, so a locked patient resolves its name from cache.
const { data: patients, isFetching: isFetchingPatients } = useQuery({
  queryKey: ['patientsForFilters', []],
  queryFn: () => patientsApi.indexForFilters([]),
  placeholderData: keepPreviousData,
});

// Editing a block of a patient no longer in the picker (archived) keeps it selectable.
const patientItems = computed<PatientFilter[]>(() => {
  const list = patients.value ?? [];
  const block = props.block;
  if (!block || list.some((patient) => patient.id === block.patientId)) return list;

  return [{ id: block.patientId, fullName: block.patientFullName, dni: block.patientDni }, ...list];
});

const careerItems = computed(() => {
  const accessible = accessibleCareers.value;
  const selected = props.block?.careers ?? [];
  const missing = allCareers.filter((career) => (
    selected.includes(career.value) && !accessible.some((item) => item.value === career.value)
  ));

  return [...accessible, ...missing];
});

function careerTitle(value: string | null) {
  return allCareers.find((career) => career.value === value)?.title ?? '';
}

function humanDate(date: string) {
  const [, month, day] = date.split('-');

  return `${day}/${month}`;
}

// The candidate range, or undefined while the form is incomplete or inverted.
function candidateRange(form: BlockFormValues): { startsAt: string; endsAt: string } | undefined {
  const parts = [form.startDate, form.startTime, form.endDate, form.endTime];
  if (parts.some((part) => !part)) return undefined;
  const startsAt = `${form.startDate}T${form.startTime}`;
  const endsAt = `${form.endDate}T${form.endTime}`;

  return endsAt > startsAt ? { startsAt, endsAt } : undefined;
}

const conflictParams = computed<ScheduleBlockConflictsParams | undefined>(() => {
  const range = candidateRange(values);
  if (!values.patientId || !range) return undefined;

  return { patientId: values.patientId, ...range, careers: values.careers ?? [], exceptId: props.block?.id };
});
const debouncedConflictParams = useDebouncedRef(conflictParams);

const { data: conflicts, isFetching: isFetchingConflicts } = useQuery({
  queryKey: ['patientScheduleBlockConflicts', debouncedConflictParams],
  queryFn: () => patientScheduleBlocksApi.conflicts(debouncedConflictParams.value!),
  enabled: computed(() => !!debouncedConflictParams.value),
  placeholderData: keepPreviousData,
});

const visibleConflicts = computed(() => (conflicts.value?.shifts ?? []).slice(0, MAX_VISIBLE_CONFLICTS));
const hiddenConflictsCount = computed(() => (
  Math.max(0, (conflicts.value?.totalCount ?? 0) - visibleConflicts.value.length)
));

const errorMessage = ref<string | null>(null);
const showDeleteDialog = ref(false);

function invalidateCalendars() {
  queryClient.invalidateQueries({ queryKey: ['shiftsAsCalendar'] });
  queryClient.invalidateQueries({ queryKey: ['shiftsCalendarEvents'] });
}

function finish() {
  invalidateCalendars();
  emit('success');
  emit('close');
}

function payload() {
  return {
    startsAt: `${values.startDate}T${values.startTime}`,
    endsAt: `${values.endDate}T${values.endTime}`,
    description: values.description.trim(),
    careers: values.careers ?? [],
  };
}

const { mutate: saveBlock, isPending: isSaving } = useMutation({
  mutationFn: () => (props.block ?
    patientScheduleBlocksApi.update(props.block.id, payload()) :
    patientScheduleBlocksApi.create({ patientId: values.patientId!, ...payload() })),
  onSuccess: finish,
  onError: (error: unknown) => {
    errorMessage.value = requestErrorMessage(error, t('scheduleBlocks.saveError'));
  },
});

const { mutate: destroyBlock, isPending: isDeleting } = useMutation({
  mutationFn: () => patientScheduleBlocksApi.destroy(props.block!.id),
  onSuccess: finish,
  onError: (error: unknown) => {
    showDeleteDialog.value = false;
    errorMessage.value = requestErrorMessage(error, t('scheduleBlocks.deleteError'));
  },
});

const onSubmit = handleSubmit(() => {
  errorMessage.value = null;
  saveBlock();
});
</script>
<template>
  <v-card
    class="w-100 pa-4"
    flat
    data-testid="patient-schedule-block-form"
  >
    <v-card-title class="d-flex align-center ga-2">
      <v-icon icon="mdi-calendar-lock" />
      {{ isEdit ? t('scheduleBlocks.editTitle') : t('scheduleBlocks.newTitle') }}
      <v-spacer />
      <create-kind-toggle
        v-if="showShiftToggle && !isEdit"
        model-value="block"
        @update:model-value="$event === 'shift' && emit('switchToShift')"
      />
      <span
        v-if="block?.createdByName"
        class="text-caption text-medium-emphasis font-weight-regular"
      >
        {{ t('scheduleBlocks.createdBy', { name: block.createdByName }) }}
      </span>
    </v-card-title>
    <v-form
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <v-row class="mx-2 mt-3">
        <v-autocomplete
          v-model="patientId"
          v-bind="patientIdAttrs"
          class="px-2 py-0 v-col-12"
          variant="outlined"
          item-title="fullName"
          item-value="id"
          :label="t('scheduleBlocks.patient')"
          :items="patientItems"
          :loading="isFetchingPatients"
          :custom-filter="nameFilter"
          :readonly="lockPatient || readonly"
          :error-messages="errors.patientId"
          data-testid="block-patient"
        />
      </v-row>
      <v-row class="mx-2">
        <v-text-field
          v-model="startDate"
          v-bind="startDateAttrs"
          type="date"
          variant="outlined"
          class="px-2 py-0 v-col-12 v-col-sm-6"
          :label="t('scheduleBlocks.startDate')"
          :readonly="readonly"
          :error-messages="errors.startDate"
          data-testid="block-start-date"
        />
        <time-input
          v-model="startTime"
          class="px-2 py-0 v-col-12 v-col-sm-6"
          :label="t('scheduleBlocks.startTime')"
          :disabled="readonly"
          :error-messages="errors.startTime"
          density="default"
        />
      </v-row>
      <v-row class="mx-2">
        <v-text-field
          v-model="endDate"
          v-bind="endDateAttrs"
          type="date"
          variant="outlined"
          class="px-2 py-0 v-col-12 v-col-sm-6"
          :label="t('scheduleBlocks.endDate')"
          :readonly="readonly"
          :error-messages="errors.endDate"
          data-testid="block-end-date"
        />
        <time-input
          v-model="endTime"
          class="px-2 py-0 v-col-12 v-col-sm-6"
          :label="t('scheduleBlocks.endTime')"
          :disabled="readonly"
          :error-messages="errors.endTime"
          density="default"
        />
      </v-row>
      <v-row class="mx-2">
        <v-textarea
          v-model="description"
          v-bind="descriptionAttrs"
          class="px-2 py-0 v-col-12"
          variant="outlined"
          rows="2"
          auto-grow
          :counter="DESCRIPTION_MAX_LENGTH"
          :label="t('scheduleBlocks.description')"
          :hint="t('scheduleBlocks.descriptionHint')"
          :readonly="readonly"
          :error-messages="errors.description"
          data-testid="block-description"
        />
      </v-row>
      <v-row class="mx-2">
        <v-select
          v-model="careers"
          v-bind="careersAttrs"
          class="px-2 py-0 v-col-12"
          variant="outlined"
          multiple
          chips
          closable-chips
          clearable
          :items="careerItems"
          item-title="title"
          item-value="value"
          :label="t('scheduleBlocks.careers')"
          :hint="t('scheduleBlocks.careersHint')"
          persistent-hint
          :readonly="readonly"
          data-testid="block-careers"
        />
      </v-row>

      <v-alert
        v-if="debouncedConflictParams"
        type="info"
        variant="tonal"
        density="compact"
        class="mx-4 mt-6"
        data-testid="block-conflicts"
      >
        <p class="font-weight-bold">
          {{ t('scheduleBlocks.conflicts.title') }}
        </p>
        <p
          v-if="isFetchingConflicts && !conflicts"
          class="text-caption"
        >
          {{ t('scheduleBlocks.conflicts.loading') }}
        </p>
        <p
          v-else-if="!visibleConflicts.length"
          class="text-caption"
        >
          {{ t('scheduleBlocks.conflicts.none') }}
        </p>
        <template v-else>
          <p class="text-caption mb-1">
            {{ t('scheduleBlocks.conflicts.info') }}
          </p>
          <div
            v-for="shift in visibleConflicts"
            :key="shift.id"
            class="d-flex align-center ga-2 py-1"
          >
            <v-chip
              size="x-small"
              :color="getCareerColor(shift.caregiverCareer ?? '')"
              variant="flat"
            >
              {{ careerTitle(shift.caregiverCareer) || '—' }}
            </v-chip>
            <span class="text-body-2">{{ shift.caregiverFullName }}</span>
            <span class="text-caption">{{ humanDate(shift.date) }} {{ shift.humanTime }}</span>
          </div>
          <p
            v-if="hiddenConflictsCount > 0"
            class="text-caption"
          >
            {{ t('scheduleBlocks.conflicts.more', { count: hiddenConflictsCount }) }}
          </p>
        </template>
      </v-alert>

      <v-alert
        v-if="errorMessage"
        type="error"
        variant="tonal"
        closable
        class="mx-4 mt-2"
        data-testid="block-error"
        @click:close="errorMessage = null"
      >
        {{ errorMessage }}
      </v-alert>

      <v-card-actions class="mt-2">
        <v-btn
          variant="text"
          @click="emit('close')"
        >
          {{ t('scheduleBlocks.close') }}
        </v-btn>
        <v-btn
          v-if="canDestroy"
          color="error"
          variant="text"
          data-testid="block-delete"
          @click="showDeleteDialog = true"
        >
          {{ t('scheduleBlocks.delete') }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="canSave"
          color="primary"
          variant="flat"
          type="submit"
          :loading="isSaving"
          data-testid="block-submit"
        >
          {{ isEdit ? t('scheduleBlocks.submitEdit') : t('scheduleBlocks.submitNew') }}
        </v-btn>
      </v-card-actions>
    </v-form>

    <responsive-dialog
      v-model="showDeleteDialog"
      max-width="440"
      :mobile-fullscreen="false"
    >
      <v-card class="pa-4">
        <v-card-title class="d-flex align-center ga-2">
          <v-icon
            icon="mdi-alert-outline"
            color="error"
          />
          {{ t('scheduleBlocks.deleteTitle') }}
        </v-card-title>
        <v-card-text>{{ t('scheduleBlocks.deleteConfirmation') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            @click="showDeleteDialog = false"
          >
            {{ t('scheduleBlocks.cancel') }}
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="isDeleting"
            data-testid="block-delete-confirm"
            @click="destroyBlock()"
          >
            {{ t('scheduleBlocks.deleteConfirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </responsive-dialog>
  </v-card>
</template>
